import React, { useEffect, useState } from "react";
import { ProgressBar } from "react-loader-spinner";
import { useLocation } from "react-router-dom";
import Footer from "units/dashboard/components/Footer";
import Header from "units/dashboard/components/Header";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "units/user/components/UserSidebar";
import { charge, paymentGatewayList } from "./services/paymentServic";
import { useSelector } from "react-redux";
import {
  CreditCard, ShieldCheck, Check, Landmark, AlertTriangle,
  Info, Receipt,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Helpers
 * ================================================================ */
const cleanString = (str) =>
  String(str)
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/[^\d۰-۹٬,]/g, "");

const convertPersianToEnglish = (str) => {
  const cleaned = cleanString(str);
  return cleaned
    .replace(/[۰-۹]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x6f0))
    .replace(/٬|,/g, "");
};

const toPersianNumber = (num) => Number(num || 0).toLocaleString("fa-IR");

/* ================================================================
 *  Gateway Logo
 * ================================================================ */
const GatewayLogo = ({ name }) => {
  const src =
    name === "zarinpal"
      ? "/Zarrinpal-Logo.png"
      : name === "zibal"
        ? "/zibal.png"
        : null;

  if (!src) {
    return (
      <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40 text-amber-600 dark:text-amber-300">
        <Landmark size={18} />
      </span>
    );
  }

  return (
    <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-white/70 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 p-1 overflow-hidden">
      <img src={src} alt={name} className="w-full h-full object-contain" />
    </span>
  );
};

/* ================================================================
 *  Main
 * ================================================================ */
const PaymentGatewaySelectorInner = () => {
  const { t, i18n } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletMessage, setWalletMessage] = useState("");
  const [list, setList] = useState([]);
  const [chargeInfo, setChargeInfo] = useState({});
  const [rules, setRules] = useState(false);
  const accessToken = useSelector((state) => state.auth.accessToken);

  const location = useLocation();
  const rawFinalPrice = location.state?.finalPrice || "۰";
  const finalPriceString = convertPersianToEnglish(String(rawFinalPrice));
  const finalPriceNumber = parseInt(finalPriceString, 10);
  const isEnglish = (i18n.language === 'en');
  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const res = await paymentGatewayList(accessToken);
        if (res?.ok) setList(res?.data);
      } catch (error) { }
    };
    fetchGateways();
  }, [accessToken]);

  const handleSelect = (method, gatewayName = null) => {
    setSelectedMethod(method);
    if (gatewayName) setSelectedGateway(gatewayName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedMethod === "gateway" && selectedGateway) {
        const res = await charge({
          accessToken,
          gateway: selectedGateway,
          amount: finalPriceNumber,
        });
        if (res?.ok) {
          setChargeInfo(res?.data);
          window.location.href = res.data.url;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isSubmitDisabled =
    !selectedMethod ||
    (selectedMethod === "gateway" && !selectedGateway) ||
    !rules;

  if (loading) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl">
          <Header />
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <ProgressBar visible height="80" width="80" barColor="#14B8A6" borderColor="#FCD34D" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="px-3 sm:px-4 md:px-6 py-5 sm:py-8">

              {/* ============ Page Header ============ */}
              <div className="max-w-2xl mx-auto mb-5">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <CreditCard size={18} className="text-amber-500" />
                  </span>
                  <div>
                    <h1 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-amber-100">
                      {t("paymentGateway.pageTitle")}
                    </h1>
                    <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t("paymentGateway.pageSubtitle")}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                {/* ============ Amount strip (inline, no card) ============ */}
                <div className="flex items-center justify-between gap-3 rounded-xl bg-teal-500/5 dark:bg-teal-400/5 border border-teal-500/15 dark:border-teal-400/15 px-4 py-3 mb-5">
                  <span className="flex items-center gap-2 text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400">
                    <Receipt size={13} className="text-teal-600/80 dark:text-teal-300/80" />
                    {t("paymentGateway.payableAmount")}
                  </span>
                  <span className={`text-sm sm:text-base font-extrabold text-amber-600 dark:text-amber-300 tabular-nums ${isEnglish && 'font-sans'}`}>
                    {finalPriceNumber.toLocaleString('en-US')}{" "}
                    <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                      {t("paymentGateway.currency")}
                    </span>
                  </span>
                </div>

                {/* ============ Gateways list ============ */}
                <div>
                  <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-2 tracking-wide">
                    {t("paymentGateway.gatewaysTitle")}
                  </p>

                  {list && list.length > 0 ? (
                    <div className="space-y-2">
                      {list.map((ipgs, index) => {
                        const isSelected =
                          selectedMethod === "gateway" && selectedGateway === ipgs;

                        const gatewayName = t(
                          `paymentGateway.gatewayNames.${ipgs}`,
                          { defaultValue: ipgs }
                        );

                        return (
                          <label
                            key={index}
                            onClick={() => handleSelect("gateway", ipgs)}
                            className={`
                              group relative
                              flex items-center gap-3
                              rounded-xl px-3 py-3
                              cursor-pointer
                              border transition-all duration-200
                              ${isSelected
                                ? "bg-gradient-to-l from-teal-500/15 to-amber-300/10 border-teal-400/60 ring-2 ring-teal-400/25 shadow-[0_6px_20px_-8px_rgba(20,184,166,0.6)]"
                                : "bg-white/60 dark:bg-white/5 border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/5"
                              }
                            `}
                          >
                            {/* Custom radio */}
                            <span
                              className={`
                                shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center
                                transition-all duration-200
                                ${isSelected
                                  ? "border-teal-500 bg-teal-500"
                                  : "border-slate-300 dark:border-slate-600"
                                }
                              `}
                            >
                              {isSelected && (
                                <Check size={11} className="text-white" strokeWidth={3} />
                              )}
                            </span>

                            <GatewayLogo name={ipgs} />

                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-amber-100 truncate">
                                {t("paymentGateway.gatewayPrefix", { name: gatewayName })}
                              </p>
                              <p className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                {t("paymentGateway.secureOnlinePayment")}
                              </p>
                            </div>

                            <input
                              type="radio"
                              name="gateway"
                              value={ipgs}
                              checked={isSelected}
                              readOnly
                              className="hidden"
                            />
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-start gap-3 rounded-xl bg-rose-500/10 border border-rose-400/40 px-4 py-4">
                      <span className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-500">
                        <AlertTriangle size={16} />
                      </span>
                      <div>
                        <p className="text-xs font-bold text-rose-700 dark:text-rose-300">
                          {t("paymentGateway.noGatewayTitle")}
                        </p>
                        <p className="text-[11px] text-rose-600/80 dark:text-rose-300/70 mt-0.5">
                          {t("paymentGateway.noGatewaySubtitle")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ============ Rules checkbox ============ */}
                <label className="mt-4 flex items-start gap-3 cursor-pointer group">
                  <span
                    className={`
                      mt-0.5 shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center
                      transition-all duration-200
                      ${rules
                        ? "border-teal-500 bg-teal-500"
                        : "border-slate-300 dark:border-slate-600 group-hover:border-amber-400"
                      }
                    `}
                  >
                    {rules && <Check size={12} className="text-white" strokeWidth={3} />}
                  </span>

                  <span className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-5">
                    {t("paymentGateway.termsAcceptPrefix")}{" "}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-amber-600 dark:text-amber-300 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("paymentGateway.termsLink")}
                    </a>{" "}
                    {t("paymentGateway.termsAcceptSuffix")}
                  </span>

                  <input
                    type="checkbox"
                    checked={rules}
                    onChange={() => setRules(!rules)}
                    className="hidden"
                  />
                </label>

                {/* ============ Info hint ============ */}
                <div className="mt-3 flex items-start gap-2 px-1">
                  <Info size={12} className="text-teal-600/70 dark:text-teal-300/70 mt-0.5 shrink-0" />
                  <p className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 leading-5">
                    {t("paymentGateway.infoHint")}
                  </p>
                </div>

                {/* ============ Submit ============ */}
                <button
                  type="submit"
                  disabled={isSubmitDisabled}
                  className="
                    group relative overflow-hidden
                    mt-5 w-full
                    flex items-center justify-center gap-2
                    py-3.5 px-4 rounded-xl
                    text-sm font-bold text-white
                    bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                    shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                    hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                    hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)]
                    hover:scale-[1.005] active:scale-[0.995]
                    transition-all duration-300
                    disabled:opacity-40 disabled:cursor-not-allowed
                    disabled:hover:scale-100 disabled:hover:shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                  "
                >
                  {!isSubmitDisabled && (
                    <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  )}
                  <CreditCard size={15} className="relative" />
                  <span className="relative">
                    {!rules
                      ? t("paymentGateway.buttonTermsRequired")
                      : !selectedGateway
                        ? t("paymentGateway.buttonSelectGateway")
                        : t("paymentGateway.buttonPay")}
                  </span>
                </button>

                {/* ============ Trust line ============ */}
                <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                  <ShieldCheck size={11} className="text-emerald-500/70" />
                  {t("paymentGateway.trustLine")}
                </div>
              </form>

              <div className="max-w-2xl mx-auto mt-8">
                <Footer />
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

const PaymentGatewaySelector = () => (
  <SidebarProvider>
    <PaymentGatewaySelectorInner />
  </SidebarProvider>
);

export default PaymentGatewaySelector;